import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { formatCurrency, formatDate, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    TrendingUp,
    TrendingDown,
    DollarSign,
    Package,
    Calculator,
    Calendar,
    Edit,
    RefreshCw
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function Reports() {
    const { hasPermission } = useAuth();
    const canViewPrixAchat = hasPermission('view_prix_achat');
    const canEditPrixAchat = hasPermission('edit_prix_achat');
    const [reportData, setReportData] = useState({ orders: [], summary: {} });
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [editingOrder, setEditingOrder] = useState(null);
    const [prixAchat, setPrixAchat] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            
            const response = await api.get('/reports/profit', { params });
            setReportData(response.data);
        } catch (error) {
            console.error('Error fetching report:', error);
            toast.error('Erreur lors du chargement du rapport');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleEditPrixAchat = (order) => {
        setEditingOrder(order);
        setPrixAchat(order.prix_achat || '');
        setDialogOpen(true);
    };

    const handleSavePrixAchat = async () => {
        if (!editingOrder) return;
        setSubmitting(true);
        
        try {
            await api.put(`/orders/${editingOrder.id}/prix-achat?prix_achat=${parseFloat(prixAchat) || 0}`);
            toast.success('Prix d\'achat mis à jour');
            setDialogOpen(false);
            fetchReport();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        } finally {
            setSubmitting(false);
        }
    };

    const handleFilter = () => {
        fetchReport();
    };

    const handleClearFilter = () => {
        setStartDate('');
        setEndDate('');
    };

    const summary = reportData.summary || {};

    const statCards = [
        {
            title: 'Chiffre d\'Affaires',
            value: formatCurrency(summary.total_revenue || 0),
            icon: DollarSign,
            color: 'text-blue-600',
            bgColor: 'bg-blue-100 dark:bg-blue-900/30',
            show: true,
        },
        {
            title: 'Coût Total (Achat)',
            value: formatCurrency(summary.total_cost || 0),
            icon: TrendingDown,
            color: 'text-red-600',
            bgColor: 'bg-red-100 dark:bg-red-900/30',
            show: canViewPrixAchat,
        },
        {
            title: 'Bénéfice Net',
            value: formatCurrency(summary.total_profit || 0),
            icon: TrendingUp,
            color: 'text-emerald-600',
            bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
            show: canViewPrixAchat,
        },
        {
            title: 'Commandes',
            value: summary.total_orders || 0,
            icon: Package,
            color: 'text-primary',
            bgColor: 'bg-primary/10',
            show: true,
        },
    ].filter(card => card.show);

    return (
        <Layout>
            <div className="space-y-6" data-testid="reports-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-semibold tracking-tight">Rapports</h1>
                        <p className="text-muted-foreground mt-1">
                            Analysez vos bénéfices par commande et par période
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={fetchReport}
                        disabled={loading}
                        data-testid="refresh-btn"
                    >
                        <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
                        Actualiser
                    </Button>
                </div>

                {/* Date Filter */}
                <Card className="card-luxury">
                    <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row gap-4 items-end">
                            <div className="flex-1 space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    Date de début
                                </Label>
                                <Input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="input-luxury"
                                    data-testid="start-date-input"
                                />
                            </div>
                            <div className="flex-1 space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    Date de fin
                                </Label>
                                <Input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="input-luxury"
                                    data-testid="end-date-input"
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button onClick={handleFilter} className="btn-luxury" data-testid="filter-btn">
                                    Filtrer
                                </Button>
                                <Button variant="outline" onClick={handleClearFilter} data-testid="clear-filter-btn">
                                    Effacer
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {statCards.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <Card key={index} className="card-luxury hover-lift" data-testid={`stat-card-${index}`}>
                                <CardContent className="p-6">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-sm text-muted-foreground">{stat.title}</p>
                                            <p className="text-2xl font-semibold mt-1">{stat.value}</p>
                                        </div>
                                        <div className={cn('w-12 h-12 rounded-[2px] flex items-center justify-center', stat.bgColor)}>
                                            <Icon className={cn('w-6 h-6', stat.color)} />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Profit Summary Card */}
                {summary.total_orders > 0 && canViewPrixAchat && (
                    <Card className="card-luxury bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border-emerald-200 dark:border-emerald-800">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-300">Résumé des Bénéfices</h3>
                                    <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">
                                        Bénéfice moyen par commande: <strong>{formatCurrency(summary.average_profit || 0)}</strong>
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">
                                        {formatCurrency(summary.total_profit || 0)}
                                    </p>
                                    <p className="text-sm text-emerald-600 dark:text-emerald-400">Bénéfice Total</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Orders Table */}
                <Card className="card-luxury">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Calculator className="w-5 h-5" />
                            Détail par Commande
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="table-container">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="table-header">N° Commande</TableHead>
                                        <TableHead className="table-header">Date</TableHead>
                                        <TableHead className="table-header">Produit</TableHead>
                                        <TableHead className="table-header">Prix Vente</TableHead>
                                        {canViewPrixAchat && (
                                            <>
                                                <TableHead className="table-header">Prix Achat</TableHead>
                                                <TableHead className="table-header">Bénéfice</TableHead>
                                            </>
                                        )}
                                        {canEditPrixAchat && <TableHead className="table-header w-12"></TableHead>}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-12">
                                                <div className="flex justify-center">
                                                    <div className="spinner" />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : reportData.orders.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                                                Aucune commande trouvée pour cette période
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        reportData.orders.map((order) => {
                                            const profit = order.profit || 0;
                                            const hasPrixAchat = order.prix_achat > 0;
                                            
                                            return (
                                                <TableRow key={order.id} className="table-row">
                                                    <TableCell>
                                                        <span className="order-number font-medium">{order.n_commande}</span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm text-muted-foreground">
                                                            {formatDate(order.date_commande)}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="truncate max-w-[200px] block">{order.produit}</span>
                                                    </TableCell>
                                                    <TableCell className="font-medium">
                                                        {formatCurrency(order.prix)}
                                                    </TableCell>
                                                    {canViewPrixAchat && (
                                                        <>
                                                            <TableCell>
                                                                {hasPrixAchat ? (
                                                                    <span className="font-medium text-red-600">
                                                                        {formatCurrency(order.prix_achat)}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted-foreground italic">Non défini</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <span className={cn(
                                                                    'font-semibold',
                                                                    profit > 0 ? 'text-emerald-600' : profit < 0 ? 'text-red-600' : 'text-muted-foreground'
                                                                )}>
                                                                    {hasPrixAchat ? formatCurrency(profit) : '-'}
                                                                </span>
                                                            </TableCell>
                                                        </>
                                                    )}
                                                    {canEditPrixAchat && (
                                                        <TableCell>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handleEditPrixAchat(order)}
                                                                data-testid={`edit-prix-${order.id}`}
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                            </Button>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>

                {/* Edit Prix Achat Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Modifier le Prix d'Achat</DialogTitle>
                        </DialogHeader>
                        {editingOrder && (
                            <div className="space-y-4">
                                <div className="p-4 rounded-[2px] bg-muted/30">
                                    <p className="font-medium">{editingOrder.n_commande}</p>
                                    <p className="text-sm text-muted-foreground">{editingOrder.produit}</p>
                                    <p className="text-sm mt-2">
                                        Prix de vente: <strong>{formatCurrency(editingOrder.prix)}</strong>
                                    </p>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Prix d'Achat (DA)</Label>
                                    <Input
                                        type="number"
                                        value={prixAchat}
                                        onChange={(e) => setPrixAchat(e.target.value)}
                                        placeholder="0"
                                        className="input-luxury"
                                        data-testid="prix-achat-input"
                                    />
                                </div>

                                {prixAchat && (
                                    <div className="p-3 rounded-[2px] bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                                        <p className="text-sm text-emerald-800 dark:text-emerald-300">
                                            Bénéfice prévu: <strong>{formatCurrency(editingOrder.prix - (parseFloat(prixAchat) || 0))}</strong>
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button 
                                className="btn-luxury" 
                                onClick={handleSavePrixAchat}
                                disabled={submitting}
                            >
                                {submitting ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
